import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { engageAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, inputCls, panelCls } from '../components/ui';

// The rebuilt DM inbox (D-2026-68 D7): thread list + conversation pane.
export default function Messages() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState(null);
  const [convo, setConvo] = useState(null); // {messages, other, me}
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const activeId = params.get('t') || '';

  const loadThreads = useCallback(() => {
    engageAPI.threads().then(({ data }) => setThreads(data.threads)).catch(() => setThreads([]));
  }, []);
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (!activeId) {
      setConvo(null);
      return;
    }
    engageAPI.messages(activeId)
      .then(({ data }) => {
        setConvo(data);
        loadThreads(); // refresh unread counts after reading
      })
      .catch(() => toast.error(t('Could not load the conversation')));
  }, [activeId, loadThreads]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [convo]);

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !activeId) return;
    setBusy(true);
    try {
      const { data } = await engageAPI.send(activeId, text);
      setConvo((prev) => ({ ...prev, messages: [...prev.messages, data] }));
      setDraft('');
      loadThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!threads) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  return (
    <div>
      <h1 className="mb-5 text-lg font-semibold">{t('Messages')}</h1>
      {threads.length === 0 ? (
        <EmptyState>{t('No conversations yet — message an instructor from a course page.')}</EmptyState>
      ) : (
        <div className={`${panelCls} grid min-h-[24rem] overflow-hidden md:grid-cols-[16rem,1fr]`}>
          <aside className="border-b border-lms-border md:border-b-0 md:border-r">
            {threads.map((th) => (
              <button
                key={th.id}
                onClick={() => setParams({ t: th.id })}
                className={`block w-full border-b border-lms-border px-4 py-3 text-left last:border-b-0 ${
                  th.id === activeId ? 'bg-lms-raised' : 'hover:bg-lms-raised/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{(th.other || {}).name || '—'}</span>
                  {th.unread > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-lms-accent px-1 text-[10px] font-semibold text-lms-accent-ink lms-num">
                      {th.unread}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-lms-faint">{th.last_snippet}</div>
              </button>
            ))}
          </aside>
          <section className="flex flex-col">
            {!convo ? (
              <p className="flex flex-1 items-center justify-center text-sm text-lms-faint">{t('Pick a conversation.')}</p>
            ) : (
              <>
                <div className="border-b border-lms-border px-4 py-2.5 text-sm font-medium">
                  {(convo.other || {}).name}
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto p-4">
                  {convo.messages.map((m) => {
                    const mine = m.sender_member_id === convo.me;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                          mine ? 'bg-lms-accent text-lms-accent-ink' : 'bg-lms-raised text-lms-text'
                        }`}>
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          <div className={`mt-1 text-[10px] lms-num ${mine ? 'text-lms-accent-ink/70' : 'text-lms-faint'}`}>
                            {(m.created_at || '').slice(11, 16)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="flex gap-2 border-t border-lms-border p-3">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('Write a message…')}
                    className={inputCls}
                  />
                  <button
                    type="submit"
                    disabled={busy || !draft.trim()}
                    className="shrink-0 rounded-md bg-lms-accent px-3 py-2 text-lms-accent-ink disabled:opacity-60"
                    aria-label={t('Send')}
                  >
                    <Send size={15} />
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
